/**
 * Auth module — bearer-token sign-in against Udexon's auth-svc, proxied
 * through the gateway under `/auth/*`.
 *
 * Storage model:
 *   • access token  : in-memory only (page lifetime)
 *   • refresh token : HttpOnly Secure cookie set by the server at /auth, OR
 *                     a fallback in sessionStorage returned in the JSON body
 *                     (used when the dashboard runs cross-origin and the
 *                     cookie can't be sent automatically)
 *
 * Ground-up Impersonation Redesign:
 *   • Main superadmin session (tokens + metadata) is kept completely separate
 *     from the active impersonated user context.
 *   • The target user's ID is persisted to sessionStorage.
 *   • On page reloads/refreshes, the superadmin's session is restored first,
 *     and the target user's impersonation token is re-minted dynamically.
 *   • Exiting impersonation discards the target context, cleanly falling back
 *     to the superadmin's active session without logouts.
 *
 * @module auth
 */

export interface MeResponse {
	id: string;
	username?: string;
	email?: string;
	role: string;
	status: string;
	driver_status?: string;
	tenant_id?: string;
	tenant_slug?: string;
	/** Enabled platform capabilities for the user's tenant (e.g. ["fleet","shuttle"]). */
	capabilities?: string[];
	/** Active user effective permissions. */
	permissions?: string[];
}

interface TokenResponse {
	access_token: string;
	refresh_token: string;
	token_type: string;
	expires_in: number;
}

interface AuthEnvelope {
	user: MeResponse;
	tokens: TokenResponse;
}

// ─── Active Sessions State ──────────────────────────────────────────────────

// Main authenticated user (e.g. Admin or Superadmin)
let mainToken: string | null = null;
let mainExpiresAt = 0; // epoch ms
let mainMe: MeResponse | null = null;
let mainRefreshToken: string | null = null;

// Impersonation state (if active)
let impToken: string | null = null;
let impExpiresAt = 0; // epoch ms
let impMe: MeResponse | null = null;
let impTargetUserId: string | null = null;

const REFRESH_KEY = "udexon_refresh_fallback";
const IMP_USER_ID_KEY = "udx-impersonate-target-user-id";

// True once a session has been established this page-load (login or successful
// refresh). Gates the "session expired" prompt so it only appears when an
// active session dies mid-use — not on a normal first-visit login.
let hadSession = false;

// Initialize sessions state from storage
function loadStoredState(): void {
	try {
		mainRefreshToken = sessionStorage.getItem(REFRESH_KEY);
		impTargetUserId = sessionStorage.getItem(IMP_USER_ID_KEY);
	} catch {
		/* ignore */
	}
}
loadStoredState();

function persistMainRefreshToken(rt: string | null): void {
	mainRefreshToken = rt;
	try {
		if (rt) {
			sessionStorage.setItem(REFRESH_KEY, rt);
		} else {
			sessionStorage.removeItem(REFRESH_KEY);
		}
	} catch {
		/* ignore quota/private-mode errors */
	}
}

function clearAllLocal(): void {
	mainToken = null;
	mainExpiresAt = 0;
	mainMe = null;
	persistMainRefreshToken(null);

	impToken = null;
	impExpiresAt = 0;
	impMe = null;
	impTargetUserId = null;
	try {
		sessionStorage.removeItem(IMP_USER_ID_KEY);
	} catch {
		/* ignore */
	}
}

// ─── Refreshing / Refresh Main Session ──────────────────────────────────────

// Single-flight guard. Refresh tokens rotate server-side (each /auth/refresh
// revokes the presented token and issues a new one) with reuse detection that
// nukes the whole token family if a revoked token is replayed. Firing several
// refreshes concurrently — which happens whenever a backgrounded tab resumes
// and its queued requests all hit an expired access token at once — makes the
// racers replay the just-rotated token and trip reuse detection, logging the
// user out or leaving a stale token behind. Collapsing concurrent callers onto
// one in-flight request removes the race.
let refreshInFlight: Promise<boolean> | null = null;

function refreshMainSession(): Promise<boolean> {
	if (refreshInFlight) return refreshInFlight;
	refreshInFlight = doRefreshMainSession().finally(() => {
		refreshInFlight = null;
	});
	return refreshInFlight;
}

async function doRefreshMainSession(): Promise<boolean> {
	const rt = mainRefreshToken || sessionStorage.getItem(REFRESH_KEY);
	const body: Record<string, string> = {};
	if (rt) body.refresh_token = rt;
	try {
		const res = await fetch("/auth/refresh", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify(body),
		});
		if (!res.ok) {
			clearAllLocal();
			return false;
		}
		const env = (await res.json()) as AuthEnvelope;
		mainToken = env.tokens.access_token;
		mainExpiresAt = Date.now() + env.tokens.expires_in * 1000;
		mainMe = env.user;
		persistMainRefreshToken(env.tokens.refresh_token);
		hadSession = true;
		return true;
	} catch {
		clearAllLocal();
		return false;
	}
}

async function ensureFreshMainToken(): Promise<boolean> {
	if (mainToken && Date.now() < mainExpiresAt - 60_000) {
		return true;
	}
	return await refreshMainSession();
}

// ─── Impersonation Helpers ──────────────────────────────────────────────────

async function ensureFreshImpersonation(): Promise<boolean> {
	if (!impTargetUserId) return false;

	// If we already have a valid impersonated token, nothing to do
	if (impToken && Date.now() < impExpiresAt - 60_000) {
		return true;
	}

	// Ensure the superadmin/admin main session token is valid and fresh first.
	const hasMain = await ensureFreshMainToken();
	if (!hasMain || !mainToken) {
		return false;
	}

	// Request a fresh impersonation token using mainToken
	try {
		const res = await fetch("/auth/admin/impersonate", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${mainToken}`,
			},
			body: JSON.stringify({ user_id: impTargetUserId }),
		});
		if (!res.ok) {
			return false;
		}
		const data: ImpersonateResponse = await res.json();
		impToken = data.access_token;
		impExpiresAt = Date.now() + data.expires_in * 1000;
		return true;
	} catch (err) {
		console.error("Failed to refresh impersonation token:", err);
		return false;
	}
}

async function ensureFreshToken(): Promise<string | null> {
	if (isImpersonating()) {
		const success = await ensureFreshImpersonation();
		return success ? impToken : null;
	}
	const success = await ensureFreshMainToken();
	return success ? mainToken : null;
}

// ─── Public API ─────────────────────────────────────────────────────────────

/** Returns the authenticated user (or impersonated target user), or null if no session exists. */
export async function getMe(): Promise<MeResponse | null> {
	// 1. If impersonating
	if (isImpersonating()) {
		if (impMe && impToken && Date.now() < impExpiresAt) {
			return impMe;
		}
		const success = await ensureFreshImpersonation();
		if (success && impToken) {
			try {
				const res = await fetch("/auth/me", {
					headers: { Authorization: `Bearer ${impToken}` },
				});
				if (res.ok) {
					impMe = await res.json();
					return impMe;
				}
			} catch (err) {
				console.error("Failed to fetch impersonated user profile:", err);
			}
		}
		// If impersonation setup fails, clear context and log out completely
		clearAllLocal();
		return null;
	}

	// 2. Normal session
	if (mainMe && mainToken && Date.now() < mainExpiresAt) {
		return mainMe;
	}
	if (await refreshMainSession()) {
		return mainMe;
	}
	return null;
}

export async function isAuthenticated(): Promise<boolean> {
	return (await getMe()) !== null;
}

/**
 * POST /auth/login with username/email + password. Resolves with the user on
 * success; throws an Error with the server's message on failure. `tenant`
 * defaults to "udexon" (root tenant) server-side when omitted.
 */
export async function signIn(
	login: string,
	password: string,
	tenant?: string,
): Promise<MeResponse> {
	const body: Record<string, string> = { login, password };
	if (tenant?.trim()) body.tenant = tenant.trim().toLowerCase();
	const res = await fetch("/auth/login", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify(body),
	});
	if (!res.ok) {
		let msg = `HTTP ${res.status}`;
		try {
			const body = await res.json();
			msg = body.message ?? body.error ?? msg;
		} catch {
			/* ignore */
		}
		throw new Error(msg);
	}
	const env = (await res.json()) as AuthEnvelope;

	clearAllLocal();

	mainToken = env.tokens.access_token;
	mainExpiresAt = Date.now() + env.tokens.expires_in * 1000;
	mainMe = env.user;
	persistMainRefreshToken(env.tokens.refresh_token);
	hadSession = true;

	return env.user;
}

/** Revoke the session server-side and clear local state. */
export async function signOut(): Promise<void> {
	try {
		const rt = mainRefreshToken || sessionStorage.getItem(REFRESH_KEY);
		const body: Record<string, string> = {};
		if (rt) body.refresh_token = rt;
		await fetch("/auth/logout", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify(body),
		});
	} catch (err) {
		console.error("Sign out api error:", err);
	} finally {
		clearAllLocal();
	}
}

export interface ImpersonateResponse {
	access_token: string;
	token_type: string;
	expires_in: number;
	impersonated_by: string;
	target_user_id: string;
}

/**
 * Swap the session to act as `userId`. The original session is preserved
 * in memory/storage and restored via `stopImpersonating()`.
 */
export async function impersonate(
	userId: string,
): Promise<ImpersonateResponse> {
	const hasMain = await ensureFreshMainToken();
	if (!hasMain || !mainToken) {
		throw new Error("Unable to establish main session for impersonation");
	}

	const res = await fetch("/auth/admin/impersonate", {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${mainToken}`,
		},
		body: JSON.stringify({ user_id: userId }),
	});
	if (!res.ok) {
		let msg = `HTTP ${res.status}`;
		try {
			const d = await res.json();
			msg = d.error ?? d.message ?? msg;
		} catch {
			/* ignore */
		}
		throw new Error(msg);
	}
	const data: ImpersonateResponse = await res.json();

	impToken = data.access_token;
	impExpiresAt = Date.now() + data.expires_in * 1000;
	impTargetUserId = userId;
	impMe = null; // force re-fetch as target user on next getMe()

	try {
		sessionStorage.setItem(IMP_USER_ID_KEY, userId);
	} catch {
		/* ignore */
	}

	return data;
}

export function isImpersonating(): boolean {
	return impTargetUserId !== null;
}

export function stopImpersonating(): void {
	impToken = null;
	impExpiresAt = 0;
	impMe = null;
	impTargetUserId = null;
	try {
		sessionStorage.removeItem(IMP_USER_ID_KEY);
	} catch {
		/* ignore */
	}
}

// ─── Session-expired prompt ─────────────────────────────────────────────────

let sessionExpiredShown = false;

/**
 * Show an un-skippable "session expired" modal that forces re-login. Called
 * when an active session dies mid-use (a refresh fails on a real API call), so
 * the user gets an explicit prompt instead of the app silently 401-ing in the
 * background. Deliberately a custom overlay — not window.confirm/alert — with no
 * close button, no backdrop dismiss, and no ESC handler: the only way out is to
 * log in again. Idempotent and gated on a session having actually existed.
 */
function notifySessionExpired(): void {
	if (!hadSession || sessionExpiredShown) return;
	if (typeof document === "undefined") return;
	sessionExpiredShown = true;

	const overlay = document.createElement("div");
	overlay.id = "session-expired-overlay";
	overlay.setAttribute("role", "alertdialog");
	overlay.setAttribute("aria-modal", "true");
	overlay.style.cssText =
		"position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;" +
		"justify-content:center;background:rgba(3,6,12,0.66);backdrop-filter:blur(3px);" +
		"font-family:inherit;";
	overlay.innerHTML = `
    <div style="width:min(92vw,380px);background:var(--c-surface,var(--c-bg,#1b1f27));
                color:var(--c-text,#e8edf3);border:1px solid var(--c-border,#2a2f3a);
                border-radius:14px;padding:26px 24px;box-shadow:0 24px 70px rgba(0,0,0,0.55);
                text-align:center;">
      <div style="font-size:17px;font-weight:600;margin-bottom:8px;">Session expired</div>
      <div style="font-size:14px;color:var(--c-text-muted,#9aa4b2);line-height:1.55;margin-bottom:22px;">
        Your session has ended for security. Please log in again to continue.
      </div>
      <button id="session-expired-relogin" type="button"
              style="width:100%;padding:11px 16px;border:none;border-radius:9px;
                     background:var(--c-accent,#1e88e5);color:#fff;font-size:15px;
                     font-weight:600;cursor:pointer;">Log in again</button>
    </div>`;

	document.body.appendChild(overlay);
	const btn = overlay.querySelector<HTMLButtonElement>(
		"#session-expired-relogin",
	);
	btn?.addEventListener("click", () => {
		// Reload: on next boot getMe() finds no session → the normal login form.
		window.location.reload();
	});
	btn?.focus();
}

/**
 * Wrapper around fetch() that attaches the bearer access token. On 401 it
 * tries to refresh once, then retries; if that fails, local state is cleared
 * and the response is returned to the caller.
 */
export async function apiFetch(
	input: string,
	init: RequestInit = {},
): Promise<Response> {
	const headers = new Headers(init.headers);
	const token = await ensureFreshToken();
	if (token) headers.set("Authorization", `Bearer ${token}`);

	const opts: RequestInit = { ...init, headers, credentials: "include" };
	let res = await fetch(input, opts);
	if (res.status === 401) {
		const tokenRefreshed = isImpersonating()
			? await ensureFreshImpersonation()
			: await refreshMainSession();

		if (tokenRefreshed) {
			const activeToken = isImpersonating() ? impToken : mainToken;
			const retryHeaders = new Headers(init.headers);
			if (activeToken)
				retryHeaders.set("Authorization", `Bearer ${activeToken}`);
			res = await fetch(input, {
				...init,
				headers: retryHeaders,
				credentials: "include",
			});
		} else {
			// An active session could not be refreshed → it has truly expired.
			// Clear state and force an explicit, un-skippable re-login prompt
			// instead of letting subsequent calls silently 401 in the console.
			clearAllLocal();
			notifySessionExpired();
		}
	}
	return res;
}
